import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, Filter, Zap, Layers } from 'lucide-react';
import InstallerLayout from './InstallerLayout';
import { getJobs, seedDemoJobsIfEmpty } from '../../lib/data';

export default function BrowseJobs() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setJobs(seedDemoJobsIfEmpty());
  }, []);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      const matchQ =
        q.trim().length === 0 ||
        `${j.title} ${j.address}`.toLowerCase().includes(q.toLowerCase());
      const matchF = filter === 'all' || j.status === filter;
      return matchQ && matchF;
    });
  }, [jobs, q, filter]);

  const openCount = jobs.filter((j) => j.status === 'open').length;

  return (
    <InstallerLayout>
      <div className="space-y-6" data-testid="installer-browse-jobs-page">
        {/* Header */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-5 md:p-6">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_10%_0%,#ffffff44_0,transparent_40%),radial-gradient(circle_at_90%_0,#ffffff22_0,transparent_45%)]" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-100 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Job Marketplace
              </p>
              <h1 className="mt-1 text-2xl md:text-3xl font-bold">Browse available jobs</h1>
              <p className="mt-1 text-sm md:text-base text-blue-100 max-w-xl">
                Filter, search, and open job details to submit a tailored bid in just a few clicks.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 text-center">
                <div className="text-[11px] text-blue-100">Open jobs</div>
                <div className="text-xl font-semibold" data-testid="jobs-open-count">
                  {openCount}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 text-center">
                <div className="text-[11px] text-blue-100">Total in view</div>
                <div className="text-xl font-semibold" data-testid="jobs-filtered-count">
                  {filtered.length}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Zap className="w-4 h-4 text-amber-500" />
            <span>Focus on high-confidence jobs with larger square footage for best ROI.</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
            <div className="flex-1 relative max-w-xs">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                data-testid="jobs-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search address or title"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                data-testid="jobs-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All statuses</option>
                <option value="open">Open only</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        {/* Jobs grid */}
        <div className="grid gap-4 md:grid-cols-2" data-testid="jobs-grid">
          {filtered.map((j) => (
            <Link
              to={`/installer/jobs/${j.id}`}
              key={j.id}
              data-testid={`job-row-${j.id}`}
              className="group relative bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all p-4 flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="inline-flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                      {j.roofType}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {j.confidence} confidence
                    </span>
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900">{j.title}</h2>
                  <p className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                    <MapPin className="w-3 h-3 text-gray-400" />
                    <span>{j.address}</span>
                  </p>
                </div>
                <div className="text-right text-sm">
                  <div className="font-semibold text-gray-900">
                    {j.squareFeet.toLocaleString()} sq ft
                  </div>
                  <div className="mt-1 text-[11px] text-gray-500">Click to open details</div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-1 text-[11px] text-gray-500">
                <span>
                  Detection: <span className="font-medium">{j.detectionMethod}</span>
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${
                    j.status === 'open'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600'
                  }`}
                >
                  {j.status === 'open' ? 'Open for bidding' : 'Archived'}
                </span>
              </div>

              <div className="absolute inset-x-0 bottom-0 h-1 rounded-b-2xl bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-indigo-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div
            className="bg-white rounded-2xl border border-dashed border-gray-300 py-10 text-center text-gray-500 text-sm"
            data-testid="no-jobs"
          >
            No jobs match your filters. Try clearing the search or switching to "All statuses".
          </div>
        )}
      </div>
    </InstallerLayout>
  );
}
